/**
 * familiarityStore — per-day "what's already known vs not yet" map.
 *
 * Written by <EmbeddedQuiz> right after the user answers the soft quiz,
 * read by <RapidInstallation> to weight per-card dwell:
 *   - familiar  → 180ms  (quick reinforcement, brain already lit up)
 *   - unfamiliar → 280ms (longer imprint window for weak traces)
 *   - unknown   → 205ms  (middle ground)
 *
 * Stored in localStorage so it survives the quiz→tunnel handoff without
 * needing a backend round-trip. Keyed per (slug, day).
 *
 * Schema:
 *   reclaim_familiar_v1_<slug>_<day> = {
 *     ts: <ISO date>,
 *     familiar:   ["prompt text 1", ...],
 *     unfamiliar: ["prompt text 2", ...],
 *   }
 */

const STORAGE_PREFIX = "reclaim_familiar_v1_";

export function familiarityKey(slug, day) {
  return `${STORAGE_PREFIX}${slug}_${day}`;
}

export function saveFamiliarity(slug, day, { familiar = [], unfamiliar = [] }) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      familiarityKey(slug, day),
      JSON.stringify({
        ts: new Date().toISOString(),
        familiar: familiar.map(String),
        unfamiliar: unfamiliar.map(String),
      })
    );
  } catch {}
}

export function loadFamiliarity(slug, day) {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(familiarityKey(slug, day));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      familiar: Array.isArray(parsed.familiar) ? parsed.familiar : [],
      unfamiliar: Array.isArray(parsed.unfamiliar) ? parsed.unfamiliar : [],
    };
  } catch {
    return null;
  }
}

/**
 * Score a flash item against the day's familiarity map.
 * Returns one of: 'familiar' | 'unfamiliar' | 'unknown'.
 *
 * Heuristic: tokenise the card text (>3-letter content words) and the
 * known prompts the same way; whichever bucket has the most overlap wins.
 * If neither bucket has any overlap, returns 'unknown'.
 */
export function scoreFamiliarity(cardText, familiarityMap) {
  if (!cardText || !familiarityMap) return "unknown";
  const tokens = tokenise(cardText);
  if (tokens.size === 0) return "unknown";
  const famHit = bucketOverlap(tokens, familiarityMap.familiar);
  const unfamHit = bucketOverlap(tokens, familiarityMap.unfamiliar);
  if (famHit === 0 && unfamHit === 0) return "unknown";
  if (unfamHit > famHit) return "unfamiliar";
  if (famHit > unfamHit) return "familiar";
  // tied — give the unfamiliar bucket priority (more imprint time is safer
  // than less when uncertain)
  return "unfamiliar";
}

const STOP = new Set([
  "the","and","that","this","with","your","from","have","you","are","for",
  "but","not","yet","let","its","then","than","what","when","why","how",
  "into","onto","upon","just","only","about","over","under","each","more",
  "most","some","they","them","their","there","also","very","can","may",
  "will","shall","does","did","done","been","being","was","were","you're",
  "you'll","i've","i'll","we've","we'll","it's","that's","they're",
]);

function tokenise(s) {
  return new Set(
    String(s)
      .toLowerCase()
      .replace(/[^a-z0-9 ]+/g, " ")
      .split(/\s+/)
      .filter((w) => w.length > 3 && !STOP.has(w))
      .map(stem)
  );
}

// Tiny stemmer — strips common English plural / -ing / -ed endings so
// "seconds" matches "second", "running" matches "run", etc.
function stem(w) {
  if (w.length > 5 && w.endsWith("ing")) return w.slice(0, -3);
  if (w.length > 5 && w.endsWith("ed")) return w.slice(0, -2);
  if (w.length > 4 && w.endsWith("ies")) return w.slice(0, -3) + "y";
  if (w.length > 4 && w.endsWith("es")) return w.slice(0, -2);
  if (w.length > 4 && w.endsWith("s")) return w.slice(0, -1);
  return w;
}

function bucketOverlap(tokens, prompts) {
  let total = 0;
  for (const p of prompts || []) {
    const ptokens = tokenise(p);
    for (const t of ptokens) if (tokens.has(t)) total += 1;
  }
  return total;
}
